/* eslint-disable prefer-arrow-callback */
import get from 'lodash/get'
import isEmpty from 'lodash/isEmpty'
import isEqual from 'lodash/isEqual'
import noop from 'lodash/noop'
import uniqBy from 'lodash/uniqBy'
import { useCallback, useEffect, useMemo, useState, useRef } from 'react'
import { unstable_batchedUpdates as batch } from 'react-dom'
import { forceArray } from '~/common/arrayUtils'
import fetch from '~/common/fetch'
import asJqueryPlugin from '~/common/jqueryUtils'
import jsonata from '~/common/jsonata'
import { useMsg } from '~/common/localizationUtils'
import createDebugLogger from '~/common/logger'
import { toOptionShape } from '~/common/objectUtils'
import { IntlProviderFromApi } from '~/components/IntlProviders'
import VndlySelect, {
  getOptionsThatMatchValue,
  mapSimpleStringOptions,
  VndlySelectInternal
} from '~/components/VndlySelect'
import { Text } from '~/design_system'
import useDeepEqualEffect from '~/hooks/useDeepEqualEffect'
import { useEffectWithRefs, useMemoWithRefs } from '~/hooks/useEffectWithRefs'
import { useFetchOnUrlChange } from '~/hooks/useFetchOnUrlChange'
import { getPageData } from '~/hooks/useLegacyDjangoPageData'
import usePrevious from '~/hooks/usePrevious'
import { useStateRef } from '~/hooks/useStateRef'
import { getFullUrlFromApiConfig, resolveUrlTokens, useTokenizedUrl } from '../utils/tokens'

const debug = createDebugLogger('JSFSelect')

const styles = {
  option: {
    fontSize: '16px'
  },
  singleValue: {
    fontSize: '16px'
  }
}

function makeUniqShapedOptions(options) {
  return uniqBy(toOptionShape(options, { multiple: true }), v => {
    if (typeof v.value === 'object' && v.value != null && 'id' in v.value) {
      return v.value.id
    }
    return v.value
  })
}

const ONE_MINUTE = 1000 * 60
const defaultResponseCache = { ttl: ONE_MINUTE }

/**
 * A powerful and flexible select component, originally designed for `react-jsonschema-form` but adaptable for standalone use.
 * It can be populated with options from a variety of sources, including static lists and dynamic API calls. It supports
 * features like typeahead search, pre-loading, multi-select, and intelligent value-to-label resolution.
 *
 * It is the foundational select component for many parts of the application and can be used in several ways:
 * - Directly as `<JSFSelect>` for custom UI.
 * - As a controlled component via wrappers like `SmartSelect`, `HookFormJSFSelect`, or `FormikJSFSelect`.
 * - Automatically invoked by `react-jsonschema-form` when a schema property has a `ui:widget` of `select`.
 *
 * # Populating The Select Options
 *
 * There are three primary ways to provide `<option>` items to the select input.
 *
 * ---
 *
 * ### 1. Static Options from JSON Schema `enum`
 *
 * The simplest method is to define a string array in your JSON schema's `enum` field.
 *
 * @example
 * ```json
 * // schema.json
 * {
 *   "myField": {
 *     "type": "string",
 *     "title": "My Field",
 *     "enum": ["Value 1", "Value 2", "Value 3"]
 *   }
 * }
 * ```
 *
 * ---
 *
 * ### 2. Static Options from `uiSchema` (`ui:options`)
 *
 * For more control, you can provide options via the `uiSchema`. This allows you to specify distinct labels and values.
 * This is the preferred method for static options when using `react-jsonschema-form`.
 *
 * @example
 * ```json
 * // uiSchema.json
 * {
 *   "myField": {
 *     "ui:widget": "select",
 *     "ui:options": {
 *       "options": ["A", "B", "C"]
 *       // Or for custom labels and values:
 *       "options": [
 *         {"label": "Choice A", "value": "a"},
 *         {"label": "Choice B", "value": "b"},
 *         {"label": "Choice C", "value": "c"}
 *       ]
 *     }
 *   }
 * }
 * ```
 *
 * ---
 *
 * ### 3. Dynamic Options from a Remote API
 *
 * This is the most powerful feature. `JSFSelect` can fetch options dynamically from a REST API.
 * API calls are configured within `ui:options` using one of the strategies below.
 *
 * #### Common API Configuration
 * All API strategies use a configuration object with these properties:
 *
 * @param url {String} The REST API URL to call. Can contain tokens (see "Dynamic URLs with Tokens" below).
 * @param [queries] {Array<String>} An array of URL query parameters, e.g., `['foo=true', 'bar=false']`.
 * @param [transform='$'] {String} A {@link https://docs.jsonata.org/overview.html|JSONata} expression to transform the API
 *   response into an array of options (either strings or `{label, value}` objects).
 * @param [delay=250] {Number} Debounce delay (in milliseconds) before making the API call after a URL or search term changes.
 *
 * #### API Fetching Strategies
 *
 * You will often combine these strategies to achieve the desired behavior.
 *
 * For example, you can use all three strategies to achieve the following:
 * - preloadFromApi: to fetch the initial list of options when the component mounts.
 * - searchFromApi: to fetch options dynamically as the user types into the select's search input.
 * - valueLookupFromApi: to fetch the option corresponding to the field's *current value*.
 *
 * **1. `preloadFromApi`**
 *
 * Use this to fetch the initial list of options displayed when the user first clicks the select.
 * The request is made on component mount. This is ideal for populating a dropdown with a default set of choices.
 *
 * @example
 * ```json
 * "ui:options": {
 *   "preloadFromApi": {
 *     "url": "/api/countries",
 *     "transform": "records.{'label': name, 'value': id}"
 *   }
 * }
 * ```
 *
 * **2. `searchFromApi`**
 *
 * Use this to implement a "typeahead" or "autocomplete" feature. Options are fetched dynamically as the user types.
 * The special `{{value}}` token in the `url` is automatically replaced with the user's search input.
 *
 * @example
 * ```json
 * "ui:options": {
 *   "searchFromApi": {
 *     "url": "/api/users?search={{value}}",
 *     "transform": "results"
 *   }
 * }
 * ```
 *
 * **3. `valueLookupFromApi`**
 *
 * This is crucial for forms that load with an existing value (e.g., an ID) but need to display a human-readable label.
 * It fetches the full option object corresponding to the component's current `value`. The request is made on mount if a
 * value is present and can be re-triggered if dependent form data changes.
 *
 * Advanced Option:
 * It has an optional `afterStandardOptionsLoaded` property to re-fetch the value with additional filters if the context changes,
 * ensuring the selected value is still valid within the new set of options. This is useful for cases where the value is not present
 * in the initial options list but is valid within the new set of options.
 *
 * @example
 * ```json
 * "ui:options": {
 *   "valueLookupFromApi": {
 *     "url": "/api/users/{{value}}" // Fetches the user with the given ID
 *   }
 * }
 * ```
 *
 * **4. `optionsFromApi` (Legacy)**
 *
 * This is a legacy, overloaded configuration. If `preloadFromApi` or `searchFromApi` are not defined, `optionsFromApi`
 * will be used for both pre-loading and searching. For clearer, more maintainable code, it is **highly recommended** to use
 * the more specific `preloadFromApi` and `searchFromApi` configurations instead.
 *
 * ---
 *
 * # Dynamic URLs with Tokens
 *
 * Your `url` strings can be dynamic by using tokens.
 *
 * - `{{value}}`: Resolves to the current search input when using `searchFromApi`, or the component's field value for other API configs.
 * - `${<formDataPath>}`: Resolves to a value from the form's data. `<formDataPath>` is a {@link https://docs.jsonata.org/jsonata-user-group/simple-queries|JSONata} path evaluated against the entire `formData` object.
 *
 * @example
 * // URL depends on another field `country`
 * "ui:options": {
 *   "preloadFromApi": {
 *     "url": "/api/states?country_id=${country}"
 *   }
 * }
 *
 * ---
 *
 * # The Transform Property
 *
 * The `transform` property is a powerful feature that allows you to reshape API responses into the format expected by the select component.
 * It uses {@link https://docs.jsonata.org/overview.html|JSONata} expressions to transform raw API data into arrays of options.
 *
 * ## Why Transform is Needed
 *
 * API responses rarely match the exact format needed by the select component. The component expects options in one of these formats:
 * - Simple strings: `["Option 1", "Option 2", "Option 3"]`
 * - Objects with `label` and `value`: `[{"label": "Option 1", "value": "opt1"}, {"label": "Option 2", "value": "opt2"}]`
 * - Objects with `label` and `value` and other properties: `[{"label": "Option 1", "value": "opt1", "other": "other1"}, {"label": "Option 2", "value": "opt2", "other": "other2"}]`
 *
 * Most APIs return data in their own structure, so `transform` bridges this gap by reshaping the response.
 *
 * ## Common Transform Patterns
 *
 * ### 1. Extract a Nested Array
 * If your API wraps results in a container object:
 * ```json
 * // API Response:
 * {
 *   "results": ["Option A", "Option B", "Option C"],
 *   "count": 3
 * }
 *
 * // Transform:
 * "transform": "results"
 * ```
 *
 * ### 2. Map Object Properties to Label/Value
 * Transform objects with `id` and `name` fields into select options:
 * ```json
 * // API Response:
 * {
 *   "data": [
 *     {"id": 1, "name": "John Smith"},
 *     {"id": 2, "name": "Jane Doe"}
 *   ]
 * }
 *
 * // Transform:
 * "transform": "[data.{'label': name, 'value': id}]"
 * ```
 *
 * ### 3. String Concatenation for Complex Labels
 * Create compound labels by combining multiple fields:
 * ```json
 * // API Response:
 * {
 *   "results": [
 *     {"ref_id": "EMP001", "title": "Software Engineer", "department": "IT"},
 *     {"ref_id": "EMP002", "title": "Project Manager", "department": "Operations"}
 *   ]
 * }
 *
 * // Transform:
 * "transform": "[results.{'value': ref_id, 'label': ref_id & ' - ' & title}]"
 * ```
 *
 * ### 4. Complex Data Transformation with Merging
 * Preserve original data while adding label/value properties:
 * ```json
 * // Transform:
 * "transform": "$.results.($merge([$, {'value': id, 'label': name}]))[]"
 * ```
 *
 * This can be useful when you want to preserve the original data structure while adding label/value properties. The selected option
 * will still be the full original object returned from the API. You can also find this selected option in the
 * `formContext.meta.<fieldName>.selectedOption` object.
 *
 * ### 5. Filtering and Conditional Logic
 * Filter out inactive items and transform:
 * ```json
 * // Transform:
 * "transform": "[results[active=true].{'label': name, 'value': id}]"
 * ```
 *
 * ## Advanced Features
 *
 * ### Transform Context
 * The `transform` expression has access to a `transformContext` object that includes:
 * - `formContext`: The full form context from react-jsonschema-form
 * - Other contextual data that might be useful for transformation logic
 *
 * ### Default Transform
 * If no `transform` is specified, the default is `'$'`, which returns the response as-is. This works when your API already returns the expected format.
 *
 * ## Best Practices
 *
 * 1. **Keep it Simple**: Start with basic transforms and add complexity only when needed
 * 2. **Test Transforms**: Use the {@link https://try.jsonata.org/|JSONata Exerciser} to test your expressions
 * 3. **Handle Empty Results**: Ensure your transform handles empty arrays gracefully
 * 4. **Consistent Field Names**: Use consistent `label` and `value` field names across your application
 * 5. **Performance**: Complex transforms can impact performance; consider optimizing your API responses when possible
 *
 * ## JSONata Resources
 *
 * - {@link https://docs.jsonata.org/overview.html|JSONata Documentation}
 * - {@link https://try.jsonata.org/|JSONata Exerciser} (for testing expressions)
 * - {@link https://docs.jsonata.org/jsonata-user-group/simple-queries|Simple Queries Guide}
 *
 * ---
 *
 * # Enabling Multi-Select
 *
 * To enable multi-select when using `react-jsonschema-form`, your schema property must be of type `array` with `uniqueItems: true`.
 * The component will detect this schema structure and automatically enable its multi-select mode.
 *
 * For direct usage of `<JSFSelect>`, pass the `multiple={true}` prop.
 *
 * @example
 * ```json
 * "majors": {
 *   "type": "array",
 *   "title": "Majors",
 *   "uniqueItems": true,
 *   "items": {
 *     "type": "string",
 *     "enum": [] // Can be empty if options are from an API
 *   }
 * }
 * ```
 *
 */
export const JSFSelect = ({
  value: valueProp,
  options: uiOptions = {},
  onChange = noop,
  readonly,
  disabled,
  multiple,
  formContext = {},
  urlTokenMapper = formContext.urlTokenMapper,
  allowForcedValue: allowForcedValueProp,
  placeholder,
  emitOptionOnChange,
  onFetchOptions = noop,
  // Callback for when a new option(s) is selected
  onOptionChange: onOptionChangeProp = noop,
  emitOnOptionChangeWhenValueLookupLoaded: emitOnOptionChangeWhenValueLookupLoadedProp = false,
  // Callback for when the list of options change
  onMenuOptionsChange: onMenuOptionsChangeProp = noop,
  inputRef,
  responseCache = defaultResponseCache,
  ...props
}) => {
  const { id } = props
  const {
    enumOptions,
    optionsPageData,
    optionsFromApi,
    searchFromApi,
    preloadFromApi,
    valueLookupFromApi,
    styles: uiOptionsStyles = {},
    allowForcedValue = allowForcedValueProp,
    autoSelectSingleOption = false,
    emitOnOptionChangeWhenValueLookupLoaded = emitOnOptionChangeWhenValueLookupLoadedProp,
    // Can override value from ui:options
    value = valueProp,
    // Can override options from ui:options
    options: optionsFromUiOptions,
    onOptionChange = onOptionChangeProp,
    onMenuOptionsChange = onMenuOptionsChangeProp,
    excludedValues = [],
    ...moreUIOptions
  } = uiOptions
  debug(`------------`, id)
  debug(`start render`, id, { value })

  const fetchOptions = makeFetchOptions(responseCache)

  const msg = useMsg()

  const optionsFromSchemas = useMemo(
    () => getOptionsFromSchemas(enumOptions, optionsFromUiOptions, optionsPageData),
    [enumOptions, optionsFromUiOptions, optionsPageData]
  )
  const formData = formContext?.formData ?? {}
  const [selectedMultiOptions, setSelectedMultiOptions] = useState([])

  // ==================================================== //
  // Search / Typeahead Options
  // ==================================================== //
  // |
  // | These options will re-fetch whenever the resolved url changes. So if tokens are used in the url that reference
  // | other fields or the typeahead value, then when those field values change, this field will re-fetch options.
  // |
  // | Note: The {{value}} token will always refer to the typeahead input value (NOT the field form value)
  // |
  const typeaheadConfig = searchFromApi ?? optionsFromApi
  const [typeaheadValue, setTypeaheadValue] = useState('')
  const internalOnInputChange = (inputValue, { action }) =>
    batch(() => {
      if (action === 'input-change') {
        setTypeaheadValue(inputValue)
      }
      if (action === 'menu-close') {
        setTypeaheadValue('')
      }
    })
  const [typeaheadUrl, typeaheadUrlHasChanged] = useTokenizedUrl(
    typeaheadConfig,
    [{ value: typeaheadValue }, urlTokenMapper, formData, { formContext }],
    [typeaheadValue]
  )
  const [
    typeaheadOptions,
    { loading: typeaheadOptionsLoading, loadCountRef: typeaheadLoadCountRef, reset: resetTypeaheadOptions }
  ] = useFetchOptionsOnUrlChange({
    url: typeaheadValue ? typeaheadUrl : '',
    transform: typeaheadConfig?.transform,
    delay: typeaheadConfig?.delay,
    onFetchOptions,
    initial: false,
    debugFn: debug,
    debugName: `typeahead ${id}`,
    transformContext: { formContext },
    fetchOptions
  })
  const typeaheadLoading = Boolean(typeaheadValue) && (typeaheadUrlHasChanged || typeaheadOptionsLoading)

  // ==================================================== //
  // Standard Options
  // ==================================================== //
  // |
  // | These are non-searched (non-typeahead) options. Available right away and will be re-fetched whenever the
  // | resolved url changes. So if tokens are used in the url that reference other fields, then when those field
  // | values change, this field will re-fetch options.
  // |
  // | Note: The {{value}} token will always refer to the field's form value (both initial and live updated value)
  // | Note: If preloadFromApi is not defined we use optionsFromApi for standard options
  // |
  const [hasBeenHovered, setHasBeenHovered] = useState(false)

  const onSelectHover = wasHovered => {
    if (!hasBeenHovered) setHasBeenHovered(Boolean(wasHovered))
  }

  const preloadOptionsOnMenuOpen = optionsFromApi?.preload === 'onMenuOpen'
  const shouldLoadStandardOptionsOnMount = Boolean(preloadFromApi) || Boolean(optionsFromApi?.preload)
  const standardConfig = preloadFromApi ?? optionsFromApi
  const usingOptionsFromApiFallback = standardConfig === optionsFromApi
  const eitherTypeaheadOrFieldValue = usingOptionsFromApiFallback ? '' : value
  const [standardUrl, standardUrlHasChanged, standardUrlChangeCount] = useTokenizedUrl(
    standardConfig,
    [{ value: eitherTypeaheadOrFieldValue }, urlTokenMapper, formData, { formContext }],
    [eitherTypeaheadOrFieldValue]
  )

  const standardEnabled =
    // If preloading the options when the menu opens then we will wait until the select has been hovered to enable
    // eslint-disable-next-line no-nested-ternary
    preloadOptionsOnMenuOpen
      ? hasBeenHovered
      : // Else if we are loading the options on mount then we will enable them right away
      shouldLoadStandardOptionsOnMount
      ? true
      : // Else we will wait until either:
        // 1) after the user has searched and triggered a typeahead
        (typeaheadLoadCountRef.current > 0 && typeaheadValue === '') ||
        // 2) or after the standard url has changed once
        standardUrlChangeCount > 0
  debug('standardEnabled', id, {
    standardUrl,
    standardEnabled,
    typeaheadLoadCount: typeaheadLoadCountRef.current,
    standardUrlChangeCount
  })

  const shouldResetTypeaheadRef = useRef(false)
  shouldResetTypeaheadRef.current = standardUrlChangeCount >= 1

  const [
    standardOptions,
    { loading: standardOptionsLoading, loadCountRef: standardLoadCountRef, resetLoadCount: resetStandardLoadCount }
  ] = useFetchOptionsOnUrlChange({
    url: standardUrl,
    enabled: standardEnabled,
    transform: standardConfig?.transform,
    delay: standardConfig?.delay,
    onFetchOptions: (...args) => {
      onFetchOptions(...args)
      if (!usingOptionsFromApiFallback) {
        setSelectedMultiOptions([])
      }
      if (shouldResetTypeaheadRef.current) {
        shouldResetTypeaheadRef.current = false
        resetTypeaheadOptions?.()
      }
    },
    initial: shouldLoadStandardOptionsOnMount,
    debugFn: debug,
    debugName: `standard ${id}`,
    transformContext: { formContext },
    fetchOptions
  })
  const standardLoading = standardUrlHasChanged || standardOptionsLoading

  // ==================================================== //
  // Initial Value Lookup Option
  // ==================================================== //
  // |
  // | Typically used to fetch only options that match the field's value, e.g.:
  // | - You have a formData value of 4
  // | - A fetch is made to `/api/resource/4`
  // | - It returns `{ label: 'Foo Corp', value: 4 }`
  // | - Now we can display the label 'Foo Corp' in the UI, instead of just the number 4
  // |
  // | That said, you could fetch all the options, but that would be better suited for `preloadFromApi`.
  // |
  // | This will fetch ONLY ONCE on mount. Tokens may be used to reference the form value or other fields values.
  // |
  // | Note: The {{value}} token will always refer to the field's previously saved form value
  // | Note: If valueLookupFromApi is not defined we use optionsFromApi to try and lookup options
  // |
  let valueLookupConfig = valueLookupFromApi ?? optionsFromApi

  const [valueLookupOption, setValueLookupOption] = useState([])
  const [valueLookupLoadingRef, setValueLookupLoading] = useStateRef(false)
  const [valueLookupLoaded, setValueLookupLoaded] = useState(false)
  const [valueLookupFailed, setValueLookupFailed] = useState(false)

  // Here we are keeping track of if the standard options have loaded more than once. It means that they loaded
  // once on mount and then their url changed sometime later. This is important because if the standard optoins
  // are different due to other fields/context on the page changing then the valueLookupOption might no longer be
  // a valid option.
  const followupStandardOptionsLoaded =
    (value ? valueLookupLoaded : true) && // If there is a value, has it been loaded
    standardConfig &&
    standardLoadCountRef.current > (shouldLoadStandardOptionsOnMount ? 1 : 0) &&
    standardUrlChangeCount > 0
  // So if the above is true, then we will use the valueLookupFromApi.afterStandardOptionsLoaded config instead
  // This config takes into account that the valueLookup option needs to be a valid option.
  // Typically, valueLookupFromApi has a url that fetches an option by its id without any filters that filter for validity
  // Whereas, valueLookupFromApi.afterStandardOptionsLoaded will have an url that fetches options with filters that will
  // ensure that the valueLookupOption is a valid option.
  const shouldUseValueLookupConfigForAfterStandardOptionsLoaded =
    valueLookupFromApi?.afterStandardOptionsLoaded && followupStandardOptionsLoaded
  // valueLookupConfig is used to fetch the option (or multi-select options) that match the field's value.
  // It uses the valueLookupFromApi config if it exists, otherwise it uses the optionsFromApi config.
  if (shouldUseValueLookupConfigForAfterStandardOptionsLoaded) {
    valueLookupConfig = valueLookupFromApi.afterStandardOptionsLoaded
  }

  const prevValue = usePrevious(value)
  const url = resolveUrlTokens(valueLookupConfig, [{ value }, urlTokenMapper, formData, { formContext }], [value])
  const prevUrl = usePrevious(url)

  useMemoWithRefs(
    function fetchLookedupValueOptions(refs) {
      const {
        valueLookupConfig,
        valueLookupOption,
        standardOptions,
        typeaheadOptions,
        selectedMultiOptions,
        prevValue,
        url,
        prevUrl,
        shouldUseValueLookupConfigForAfterStandardOptionsLoaded
      } = refs.current
      const valueChanged = value !== prevValue
      const urlChanged = url !== prevUrl

      debug(`value change [valueLookup]`, id, {
        valueChanged,
        value,
        prevValue,
        urlChanged,
        url,
        prevUrl,
        shouldUseValueLookupConfigForAfterStandardOptionsLoaded
      })

      // Return if the value is not valid
      if (!valueLookupConfig || value == null || value === '' || (Array.isArray(value) && value.length === 0)) return

      // Return if only the url changed, but we should not use the valueLookupConfig for afterStandardOptionsLoaded
      if (!valueChanged && urlChanged && !shouldUseValueLookupConfigForAfterStandardOptionsLoaded) return

      debug(`fetch [valuelookup] - look for matching option(s)`, id, { value })

      // If we shouldn't use the valueLookupConfig for afterStandardOptionsLoaded, then we can check if the option
      // already exists in the standard options or typeahead options. If it does, then we don't need to fetch it.
      if (!shouldUseValueLookupConfigForAfterStandardOptionsLoaded) {
        // If we already have a valid option to use do not fetch it
        const mergedOptions = [...valueLookupOption, ...standardOptions, ...typeaheadOptions]

        if (!multiple) {
          const matchingOption = makeUniqShapedOptions(mergedOptions).find(
            o => isEqual(o?.value, value) || isEqual(o?.value, value?.value)
          )
          if (matchingOption) {
            debug(`fetch ABORTED [valuelookup] - matchingOption found`, id, { value, matchingOption })
            return
          }
        } else if (Array.isArray(value)) {
          const optionsToMatch = makeUniqShapedOptions([...mergedOptions, ...selectedMultiOptions])
          const matchingOptions =
            optionsToMatch.filter(
              o => value?.findIndex?.(vOption => isEqual(o?.value, vOption) || isEqual(o?.value, vOption?.value)) > -1
            ) || []
          if (matchingOptions.length === value.length) {
            debug(`fetch ABORTED [valuelookup] - matchingOptions found for multiple values`, id, {
              value,
              matchingOptions
            })
            return
          }
        }
      }
      debug(`fetch [valuelookup] - no matching option(s) found`, id)

      // Set the loading state synchronously using state-ref pattern because we want
      // loading to go to true right away.
      setValueLookupLoading(true)
      debug(`fetch START [valuelookup]`, id, url)
      fetchOptions({
        url,
        transform: valueLookupConfig.transform,
        transformContext: { formContext },
        debugName: `valuelookup ${id}`
      })
        .then(options => {
          debug(`fetch END [valuelookup]`, id, url, options)
          batch(() => {
            setValueLookupOption(options)
            setSelectedMultiOptions(options)
            // Set the loading state A-synchronously, bypassing the state-ref pattern, we want it to move in tandem
            // with the other state change in this case.
            setValueLookupLoading(false, { strategy: 'next_render' })
            setValueLookupLoaded(true)
            onFetchOptions(options)
            if (valueChanged) resetStandardLoadCount()
          })
        })
        .catch(() => {
          setValueLookupFailed(true)
        })
    },
    [value, url],
    {
      valueLookupConfig,
      valueLookupOption,
      standardOptions,
      typeaheadOptions,
      selectedMultiOptions,
      prevValue,
      url,
      prevUrl,
      shouldUseValueLookupConfigForAfterStandardOptionsLoaded
    }
  )

  // ==================================================== //
  // Make Final Value and Options
  // ==================================================== //

  const isLoading = valueLookupLoadingRef.current || standardLoading || typeaheadLoading
  debug.useOnArgsChange(`loading=${isLoading}`, id, {
    valueLookupLoading: valueLookupLoadingRef.current,
    standardLoading,
    typeaheadLoading
  })

  debug.useOnArgsChange(id, {
    followupStandardOptionsLoaded,
    shouldLoadStandardOptionsOnMount,
    standardLoadCount: standardLoadCountRef.current
  })

  // Used for 'creatable' flag on Select - if you need write-in options
  const [createdOptions, setCreatedOptions] = useState([])

  const optionsDeps = [
    optionsFromSchemas,
    standardOptions,
    valueLookupOption,
    typeaheadOptions,
    createdOptions,
    typeaheadValue,
    value,
    allowForcedValue,
    readonly,
    followupStandardOptionsLoaded,
    id,
    multiple,
    excludedValues,
    valueLookupFromApi
  ]
  const finalOptions = useMemo(() => makeFinalOptions(...optionsDeps), optionsDeps)
  useEffect(() => {
    onMenuOptionsChange(finalOptions)
  }, [finalOptions])
  const matchingOption = finalOptions.find(o => isEqual(o?.value, value) || isEqual(o?.value, value?.value))
  const mergedFinalSelected = [...finalOptions, ...selectedMultiOptions]
  const optionsToMatch = makeUniqShapedOptions(mergedFinalSelected)
  const matchingOptions =
    (multiple &&
      Array.isArray(value) &&
      optionsToMatch.filter(
        o => value?.findIndex?.(vOption => isEqual(o?.value, vOption) || isEqual(o?.value, vOption?.value)) > -1
      )) ||
    []

  // ============================ //
  // Hooks that Call `onChange`
  // ============================ //

  // This hook handles:
  // - autoSelectSingleOption
  // - clearing value that no longer has a matching option
  const { isClearingSelection } = useOnChangeCallers(
    onChange,
    onOptionChange,
    value,
    finalOptions,
    matchingOption,
    matchingOptions,
    typeaheadValue,
    isLoading,
    valueLookupFailed,
    autoSelectSingleOption,
    multiple,
    id
  )

  const internalOnChange = useOnSelect(
    onChange,
    onOptionChange,
    value,
    multiple,
    emitOptionOnChange,
    setCreatedOptions,
    setSelectedMultiOptions,
    id
  )

  // ==================================================== //
  // Other Misc Prop Helpers
  // ==================================================== //

  // This will show 'No options' when there are no results after searching instead of 'Type to search' which is misleading
  // eslint-disable-next-line no-template-curly-in-string
  const noOptionsMessage = ({ inputValue }) => {
    let no_options_msg = msg('common.ui.no_options', { noProviderFoundMsg: 'No options' })
    if (isLoading) no_options_msg = msg('common.ui.loading_ellipsis', { noProviderFoundMsg: 'Loading...' })
    // if (inputValue && (finalOptions?.length === 0 || hasOneOptionAndItIsSelected)) msg = 'No options'
    if (!inputValue && hasReactiveTypeaheadUrl(optionsFromApi))
      no_options_msg = msg('common.ui.type_to_search', { noProviderFoundMsg: 'Type to search' })
    return no_options_msg
  }

  // ==================================================== //
  // Render
  // ==================================================== //

  const finalValue = (() => {
    if (matchingOption) return matchingOption
    if (value != null) {
      if (multiple && Array.isArray(value)) {
        return value.map(vOption => {
          const found = matchingOptions?.find(smo => (smo?.value ?? smo) === (vOption?.value ?? vOption))
          return {
            label: found?.label ?? found ?? '...',
            value: vOption?.value ?? vOption
          }
        })
      }
      return { label: msg('common.ui.loading_ellipsis', { noProviderFoundMsg: 'Loading...' }), value }
    }
    return null
  })()
  const finalValueOption = isClearingSelection
    ? null
    : toOptionShape(finalValue, { multiple, missingLabelSetToValue: false })
  debug('finalProps', id, {
    matchingOption,
    value,
    finalValue,
    isClearingSelection,
    finalValueOption,
    finalOptions
  })

  useDeepEqualEffect(() => {
    if (typeof formContext?.setFieldMeta === 'function' && props.schema?._path) {
      debug('formContext.setFieldMeta', id, { _path: props.schema._path, meta: { selectedOption: finalValueOption } })
      formContext.setFieldMeta(props.schema._path, { selectedOption: finalValueOption })
    }
  }, [finalValueOption])

  // Emit onOptionChange when valueLookup loads
  const prevValueLookupLoaded = usePrevious(valueLookupLoaded)
  const prevEnumOptions = usePrevious(enumOptions)
  useEffect(() => {
    if (!emitOnOptionChangeWhenValueLookupLoaded) return
    if (prevValueLookupLoaded === false && valueLookupLoaded === true) {
      onOptionChange(finalValueOption)
    }
    // Account for enum is accessed
    else if (enumOptions && !isEqual(prevEnumOptions, enumOptions)) {
      onOptionChange(finalValueOption)
    }
  }, [
    prevValueLookupLoaded,
    valueLookupLoaded,
    enumOptions,
    prevEnumOptions,
    finalValueOption,
    emitOnOptionChangeWhenValueLookupLoaded
  ])

  const showingAllResults =
    finalOptions.length >=
    Math.max((typeaheadValue && typeaheadOptions.totalCount) ?? 0, standardOptions.totalCount ?? 0)
  const menuListMessage = !showingAllResults ? (
    <div style={{ color: 'gray', padding: '0 10px 4px', fontSize: 12, borderBottom: '1px solid #ddd' }}>
      Not showing all results. Type to search.
    </div>
  ) : null

  const Select = props.isInternalSelect ? VndlySelectInternal : VndlySelect

  return formContext?.displayAsText || props?.uiSchema?.['ui:displayAsText'] ? (
    <Text style={{ ...styles, ...uiOptionsStyles }}>
      {forceArray(finalValueOption)
        .map(o => o.label)
        .join(', ') || '-'}
    </Text>
  ) : (
    <Select
      ref={inputRef}
      value={finalValueOption}
      options={finalOptions}
      onChange={internalOnChange}
      onInputChange={internalOnInputChange}
      menuPortal={uiOptions.menuPortal}
      styles={{ ...styles, ...uiOptionsStyles }}
      isMulti={multiple}
      isLoading={isLoading}
      noOptionsMessage={(searchFromApi || optionsFromApi) && noOptionsMessage}
      placeholder={placeholder}
      menuListMessage={menuListMessage}
      {...(preloadOptionsOnMenuOpen && { onMouseEnter: onSelectHover })}
      {...props}
      {...moreUIOptions}
      disabled={readonly || disabled}
      readOnly={readonly}
    />
  )
}

// ==================================================== //
// Final Options and Final Value Makers
// ==================================================== //

function makeFinalOptions(
  optionsFromSchemas = [],
  standardOptions,
  valueLookupOption,
  typeaheadOptions,
  createdOptions,
  typeaheadValue,
  value,
  allowForcedValue,
  readOnly,
  followupStandardOptionsLoaded,
  id,
  multiple,
  excludedValues,
  valueLookupFromApi
) {
  const forcedValueOption = allowForcedValue && value !== undefined && value !== null ? forceArray(value) : []
  // Merge order matters
  const mergedOptions = [
    // If we have not yet loaded options beyond the initial mount then merge valueLookupOption,
    // otherwise no longer include the initial valueLookupOption
    ...(!followupStandardOptionsLoaded || valueLookupFromApi?.afterStandardOptionsLoaded
      ? [...forcedValueOption, ...valueLookupOption]
      : []),
    ...optionsFromSchemas,
    ...standardOptions,
    ...typeaheadOptions,
    ...createdOptions
  ]

  // Filter out any options that have been marked as excluded.
  const filteredOptions = mergedOptions.filter(option => !excludedValues.includes(option.value))

  // Options are deduped by their `value`
  const uniqShapedOptions = makeUniqShapedOptions(filteredOptions)

  debug('makeFinalOptions', id, {
    forcedValueOption,
    valueLookupOption,
    optionsFromSchemas,
    standardOptions,
    typeaheadOptions,
    createdOptions,
    mergedOptions,
    uniqShapedOptions,
    afterStandardOptionsLoaded: valueLookupFromApi?.afterStandardOptionsLoaded
  })

  // If select is readOnly, we only need the option that represents the selected value, even if we have to force it
  if (readOnly) {
    return getOptionsThatMatchValue(value, uniqShapedOptions)
  }

  return uniqShapedOptions
}

// ===================================================================== //
// onChange Callers - Hooks that will programmatically trigger `onChange`
// ===================================================================== //

function useOnSelect(
  onChange,
  onOptionChange = noop,
  value,
  multiple,
  emitOptionOnChange,
  setCreatedOptions,
  setSelectedMultiOptions,
  id
) {
  return useCallback(
    (selectedOption, { action }) => {
      batch(() => {
        let updatedFormData
        if (multiple) {
          if (selectedOption === null) {
            updatedFormData = []
          } else {
            if (action === 'create-option') {
              const createdVal = selectedOption[selectedOption.length - 1]
              setCreatedOptions(prev => [...prev, createdVal])
            }
            updatedFormData = emitOptionOnChange ? selectedOption : selectedOption.map(opt => opt?.value)
          }
          setSelectedMultiOptions(selectedOption || [])
        } else {
          if (action === 'create-option') setCreatedOptions(prev => [...prev, selectedOption])
          updatedFormData = emitOptionOnChange ? selectedOption : selectedOption?.value
        }
        debug('useOnSelect', id, {
          selectedOption,
          emitOptionOnChange,
          updatedFormData,
          multiple,
          action,
          onChange
        })
        onChange(updatedFormData)
        onOptionChange(selectedOption)
      })
    },
    [multiple, emitOptionOnChange, onChange, onOptionChange, setCreatedOptions, setSelectedMultiOptions]
  )
}

function useOnChangeCallers(
  onChange,
  onOptionChange,
  value,
  finalOptions,
  matchingOption,
  matchingOptions = [],
  typeaheadValue,
  isLoading,
  valueLookupFailed,
  autoSelectSingleOption,
  multiple,
  id
) {
  // ---------------------------- //
  // Auto-select Singular Option
  // ---------------------------- //
  // |
  // | We want to auto select an option when:
  // | - There is only one option
  // | - We have autoSelectSingleOption set to true
  // | - We don't already have that option selected
  // |
  const shouldSelectSingleOption =
    !isLoading &&
    autoSelectSingleOption &&
    finalOptions?.length === 1 &&
    !isEqual(finalOptions?.[0], value) &&
    !typeaheadValue
  const firstOptionValue = finalOptions?.[0]?.value

  // ------------------ //
  // Clear the selection
  // ------------------ //
  // |
  // | We want to clear the input when:
  // | - The value can no longer be found in the available options
  // | - There are more than 0 options
  // | - We are not in "typeahead" mode
  // | - We are not actively loading different options (We need to wait until things settle)
  // | - Not multi-select mode
  // |
  const msg = useMsg()
  const prevValue = usePrevious(value)
  const safeToClear =
    value !== undefined &&
    prevValue !== undefined &&
    !typeaheadValue &&
    !isLoading &&
    value?.label !== msg('common.ui.loading_ellipsis', { noProviderFoundMsg: 'Loading...' })
  const shouldClearSelection = !valueLookupFailed && safeToClear && !multiple && !matchingOption && value != null
  const shouldRemoveOptions =
    !valueLookupFailed && safeToClear && multiple && Array.isArray(value) && value.length !== matchingOptions.length

  debug.useOnArgsChange('onChangeCallers', id, {
    shouldSelectSingleOption,
    autoSelectSingleOption,
    finalOptionsLength: finalOptions?.length,
    finalOptionValue: finalOptions?.[0]?.value,
    typeaheadValue,
    shouldClearSelection,
    shouldRemoveOptions,
    value,
    prevValue,
    matchingOption,
    isLoading,
    multiple
  })

  useEffectWithRefs(
    refs => {
      const { onChange, shouldSelectSingleOption } = refs.current

      if (shouldSelectSingleOption) {
        debug('SELECT SINGLE', id, { autoSelectSingleOption, finalOptions, value })
        onChange(multiple ? [firstOptionValue] : firstOptionValue)
        if (finalOptions?.[0]) {
          onOptionChange(finalOptions[0])
        }
        return
      }
      if (shouldClearSelection) {
        debug('CLEAR SELECTION', id, { value, prevValue, matchingOption })
        // We send undefined to rjsf's onChange to set the formData to "blank"
        onChange(undefined)
      }
      if (shouldRemoveOptions) {
        const optionsToKeep = value.filter(
          vOption =>
            matchingOptions.findIndex(mOption => isEqual(vOption, mOption) || isEqual(vOption, mOption?.value)) > -1
        )
        debug('REMOVE OPTIONS', id, { value, prevValue, matchingOptions, optionsToKeep })
        onChange(optionsToKeep)
      }
    },
    [firstOptionValue, shouldClearSelection, shouldRemoveOptions],
    { onChange, shouldSelectSingleOption }
  )

  return { isClearingSelection: shouldClearSelection }
}

// ==================================================== //
// Non-Fetched Option Helpers
// ==================================================== //

function getOptionsFromSchemas(enumOptions, optionsFromUiOptions, optionsPageData) {
  // These two option strategies are sync so we can just set them via the state's default value
  if (!isEmpty(optionsFromUiOptions)) {
    // Extract options from the ui:options.options field
    return mapSimpleStringOptions(optionsFromUiOptions)
  }

  if (!isEmpty(enumOptions)) {
    // Extract options from the property.enum field
    return mapSimpleStringOptions(enumOptions)
  }

  if (optionsPageData) {
    // Extract selectOptions from page data if configured
    return mapPageDataToOptions(optionsPageData)
  }
  return []
}

function mapPageDataToOptions(optionsPageData) {
  const { scriptElementId = 'data', pathToArray = '', valueFromKey = 'value', labelFromKey = 'label' } = optionsPageData
  const pageData = getPageData(scriptElementId)
  const optionsFromPageData = get(pageData, pathToArray, Array.isArray(pageData) ? pageData : [pageData])
  return optionsFromPageData.map(item => ({
    value: get(item, valueFromKey),
    label: get(item, labelFromKey)
  }))
}

// ==================================================== //
// Fetched Option Helpers
// ==================================================== //

// Small wrapper around useFetchOnUrlChange to make it use our modified fetch function
function useFetchOptionsOnUrlChange({
  url,
  enabled,
  initial,
  transform,
  delay,
  onFetchOptions,
  debugFn,
  debugName,
  transformContext,
  fetchOptions
}) {
  const DEFAULT_DELAY = 250
  return useFetchOnUrlChange({
    url,
    enabled,
    initial,
    transform,
    delay: delay ?? DEFAULT_DELAY,
    fetchFn: fetchOptions,
    sideEffect: onFetchOptions,
    debugFn,
    debugName,
    transformContext
  })
}

// Fetch function geared towards fetching data and transforming into select options
function makeFetchOptions(responseCache) {
  const fetchOptions = async function fetchOptions({ url, transform, transformContext, fetchOptions }) {
    if (!url) return []

    const raw = await fetch.get(url, { responseCache, ...fetchOptions })
    const transformed = transform ? jsonata(transform).evaluate(raw, transformContext) : raw

    const mappedOptions = mapSimpleStringOptions(forceArray(transformed))
    mappedOptions.totalCount = raw?.count
    return mappedOptions
  }
  return fetchOptions
}

function hasReactiveTypeaheadUrl(optionsApi) {
  const fullUrl = getFullUrlFromApiConfig(optionsApi)
  // eslint-disable-next-line no-template-curly-in-string
  return fullUrl?.includes('${value}') || fullUrl?.includes('{{value}}')
}

export const JSFSelectUncontrolled = props => {
  const { initialValue, onChange, ...rest } = props
  const [value, setValue] = useState(initialValue)
  const valueChanged = v => {
    debug(`uncontrolled valueChanged`, v)
    setValue(v)
    onChange(v)
  }

  useEffect(() => {
    debug(`uncontrolled useEffect setValue`, initialValue)
    setValue(initialValue)
  }, [initialValue])
  return (
    <IntlProviderFromApi>
      <JSFSelect value={value} {...rest} onChange={valueChanged} />
    </IntlProviderFromApi>
  )
}
asJqueryPlugin('JSFSelect', JSFSelectUncontrolled)
